import { useEffect, useRef, useState } from 'react';
import { api, RequestFailed, type Referral, type Role } from '../api';
import { Banner, Card, Empty, Loading, Modal, Status, formatKobo, parseNairaToKobo, useAsync, when } from '../ui';

/**
 * The list, one stage at a time.
 *
 * Each tab is one thing a person does — or waits for — so what needs paying is
 * never in the same list as what is still waiting on a driver. Every tab shows
 * its count; the two that only matter when something has gone wrong appear
 * only when they hold something.
 */
interface Tab {
  key: string;
  label: string;
  /** Statuses, comma-separated, as the list API takes them. Empty is everything. */
  status: string;
  hint: string;
  tone?: 'go' | 'look' | 'bad';
  /** Shown only when it holds something, or is open. */
  quiet?: boolean;
}

const TABS: Tab[] = [
  { key: 'ready', label: 'Ready to pay', status: 'APPROVED', tone: 'go',
    hint: 'Approved and checked. Nothing is sent until you press Release approved.' },
  { key: 'review', label: 'Needs a look', status: 'FLAGGED', tone: 'look',
    hint: 'Something here needs a person: a name that is not quite the same, an account or identity shared with another driver. Open one to decide, or use the Review queue.' },
  { key: 'waiting', label: 'Waiting on driver', status: 'RECEIVED,KYC_PENDING,KYC_IN_PROGRESS',
    hint: 'Checked automatically every few minutes: when one of these drivers signs up, it moves on by itself.' },
  { key: 'inflight', label: 'Being paid', status: 'PAYOUT_INITIATED', quiet: true,
    hint: 'Sent to the bank; the result normally comes back within a minute.' },
  { key: 'problems', label: 'Payment problems', status: 'PAYOUT_FAILED,FUNDS_HELD', tone: 'bad', quiet: true,
    hint: 'The transfer did not go through. Open one to see why, and retry it from there.' },
  { key: 'paid', label: 'Paid', status: 'PAYOUT_SUCCESS', hint: 'Money received by the driver.' },
  { key: 'closed', label: 'Rejected or expired', status: 'REJECTED,EXPIRED', hint: 'Closed. Nothing more happens to these.' },
  { key: 'all', label: 'All', status: '', hint: 'Every referral, newest first.' },
];

/** Why a driver is still waiting — a filter inside the Waiting tab. */
const WAITING: { key: string; label: string }[] = [
  { key: 'not_joined', label: 'Not joined yet' },
  { key: 'phone_differs', label: 'Joined · phone differs' },
  { key: 'bvn_not_confirmed', label: 'Joined · BVN not confirmed' },
  { key: 'sent_back', label: 'Sent back to fix' },
  { key: 'unchecked', label: 'Not checked yet' },
];

const sum = (counts: Record<string, number> | undefined, status: string) =>
  status === '' ? Object.values(counts ?? {}).reduce((a, b) => a + b, 0)
    : status.split(',').reduce((a, st) => a + (counts?.[st] ?? 0), 0);

const CAN_CREATE: Role[] = ['indrive_ops', 'indrive_admin', 'zeeh_admin'];
const CAN_RELEASE: Role[] = ['indrive_admin', 'zeeh_admin'];

export function Referrals({ open, role }: { open: (id: string) => void; role: Role }) {
  const [tabKey, setTabKey] = useState<string | null>(null);
  const [sub, setSub] = useState('');
  const [batch, setBatch] = useState('');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [pages, setPages] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [accepting, setAccepting] = useState(false);
  const [rechecking, setRechecking] = useState(false);

  const counts = useAsync(() => api.referralCounts(batch || undefined), [batch]);
  const batches = useAsync(() => api.batches(), []);
  const byStatus = counts.data?.by_status;

  // Until a tab is picked, open on the first one with something to act on.
  const tab = TABS.find((t) => t.key === tabKey)
    ?? (byStatus ? TABS.find((t) => ['ready', 'review', 'waiting'].includes(t.key) && sum(byStatus, t.status) > 0) : undefined)
    ?? TABS[0]!;
  const status = tab.key === 'waiting' && sub ? `waiting:${sub}` : tab.status;

  const cursor = pages[pages.length - 1];
  const { data, error, loading, reload: reloadList } = useAsync(
    () => api.referrals(
      status.startsWith('waiting:')
        ? { status: 'RECEIVED,KYC_PENDING', waiting: status.slice(8), batch_id: batch || undefined, search: applied || undefined, cursor, limit: '50' }
        : { status: status || undefined, batch_id: batch || undefined, search: applied || undefined, cursor, limit: '50' },
    ),
    [status, batch, applied, cursor],
  );
  const reload = () => { reloadList(); counts.reload(); };
  const pick = (key: string, waiting = '') => { setTabKey(key); setSub(waiting); setPages([]); setPicked(new Set()); };
  const setStatus = (value: string) => pick(TABS.find((t) => t.status === value)?.key ?? 'all');

  // Any "waiting" filter can be re-checked in bulk; accepting a phone mismatch is for Zeeh admins on its own filter.
  const selectable = status.startsWith('waiting:') && CAN_CREATE.includes(role);
  const canAccept = status === 'waiting:phone_differs' && role === 'zeeh_admin';
  const rows = data?.referrals ?? [];
  const toggle = (id: string) =>
    setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className="stack">
      <Card flush>
        <div className="tabs" role="tablist" aria-label="Referrals by stage">
          {TABS.filter((t) => !t.quiet || t.key === tab.key || sum(byStatus, t.status) > 0).map((t) => {
            const n = sum(byStatus, t.status);
            return (
              <button key={t.key} role="tab" aria-selected={t.key === tab.key}
                      className={`tab${n > 0 && t.tone ? ` ${t.tone}` : ''}`} onClick={() => pick(t.key)}>
                {t.label}
                {byStatus && <span className="n">{n.toLocaleString()}</span>}
              </button>
            );
          })}
        </div>
        <div className="tab-hint">
          <span>{tab.hint}</span>
          {tab.key === 'waiting' && (
            <div className="subtabs" style={{ width: '100%' }}>
              <button className="subtab" aria-pressed={sub === ''} onClick={() => pick('waiting')}>
                All waiting<span className="n">{sum(byStatus, tab.status).toLocaleString()}</span>
              </button>
              {WAITING.filter((w) => (counts.data?.waiting[w.key] ?? 0) > 0 || w.key === sub).map((w) => (
                <button key={w.key} className="subtab" aria-pressed={sub === w.key} onClick={() => pick('waiting', w.key)}>
                  {w.label}<span className="n">{(counts.data?.waiting[w.key] ?? 0).toLocaleString()}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="card-head" style={{ flexWrap: 'wrap' }}>
          <select value={batch} onChange={(e) => { setBatch(e.target.value); setPages([]); setPicked(new Set()); }}
                  aria-label="Upload" style={{ width: 210 }}>
            <option value="">All uploads</option>
            {(batches.data?.batches ?? []).map((b) => (
              <option key={b.batch_id} value={b.batch_id}>
                {b.source_file} · {new Date(b.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </option>
            ))}
          </select>
          <form
            className="row grow"
            onSubmit={(e) => { e.preventDefault(); setApplied(search.trim()); setPages([]); }}
          >
            <input
              type="search"
              placeholder="Referral id, external reference, or masked account"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ minWidth: 260 }}
            />
            <button className="btn" type="submit">Search</button>
            {applied && (
              <button className="btn ghost" type="button" onClick={() => { setSearch(''); setApplied(''); setPages([]); }}>
                Clear
              </button>
            )}
          </form>
          <div className="spacer" />
          {CAN_RELEASE.includes(role) && (
            <button className="btn sm primary" onClick={() => setReleasing(true)}>Release approved…</button>
          )}
          {CAN_CREATE.includes(role) && (
            <button className="btn sm" onClick={() => setCreating(true)}>New referral</button>
          )}
          <a className="btn sm ghost" download
             href={api.exportUrl(status.startsWith('waiting:')
               ? { status: 'RECEIVED,KYC_PENDING', waiting: status.slice(8), batch_id: batch || undefined }
               : { status: status || undefined, batch_id: batch || undefined })}>
            Export CSV
          </a>
        </div>

        {selectable && rows.length > 0 && (
          <div className="card-head">
            <span className="small">
              <strong>{rows.length}</strong>{' '}
              <span className="muted">
                {canAccept
                  ? 'joined but NIBSS has their BVN under a different phone. Re-check them after a driver fixes their details, or, once inDrive confirms a driver is the right person, accept their join-link details together. Leave out any you have not confirmed.'
                  : 'are waiting on the driver. If drivers have signed up since, re-check them together: each is matched to its sign-up again and moves on if NIBSS confirms it.'}
              </span>
            </span>
            <div className="spacer" />
            <button className="btn sm" onClick={() => setPicked(picked.size === rows.length ? new Set() : new Set(rows.map((r) => r.referral_id)))}>
              {picked.size === rows.length ? 'Clear selection' : `Select all ${rows.length}`}
            </button>
            <button className={`btn sm${canAccept ? '' : ' primary'}`} disabled={picked.size === 0} onClick={() => setRechecking(true)}>
              Re-check selected{picked.size > 0 ? ` (${picked.size})` : ''}
            </button>
            {canAccept && (
              <button className="btn sm primary" disabled={picked.size === 0} onClick={() => setAccepting(true)}>
                Accept selected{picked.size > 0 ? ` (${picked.size})` : ''}…
              </button>
            )}
          </div>
        )}
        {error && <div style={{ padding: 18 }}><Banner tone="danger" title="Could not load referrals">{error}</Banner></div>}
        {loading && <Loading />}

        {!loading && data && (data.referrals.length === 0 ? (
          <Empty title={applied ? 'Nothing matches' : `Nothing in ${tab.label.toLowerCase()}`}
                 hint={applied ? 'Clear the search, or try another tab.' : 'Try another tab.'} />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {selectable && <th style={{ width: 28 }} />}
                  <th>Referral</th>
                  <th>Referrer → Referee</th>
                  <th>Status</th>
                  <th className="num">Commission</th>
                  <th>Destination</th>
                  <th>Reasons</th>
                  <th className="num">Received</th>
                </tr>
              </thead>
              <tbody>
                {data.referrals.map((r) => (
                  <Row key={r.referral_id} r={r} onOpen={() => open(r.referral_id)}
                       {...(selectable ? { checked: picked.has(r.referral_id), onToggle: () => toggle(r.referral_id) } : {})} />
                ))}
              </tbody>
            </table>
          </div>
        ))}

        <div className="card-head" style={{ borderBottom: 'none', borderTop: '1px solid var(--rule)' }}>
          <span className="small muted">
            {data ? `${data.referrals.length} shown${pages.length ? ` · page ${pages.length + 1}` : ''}` : ''}
          </span>
          <div className="spacer" />
          <button className="btn sm" disabled={pages.length === 0} onClick={() => setPages((p) => p.slice(0, -1))}>
            Previous
          </button>
          <button
            className="btn sm"
            disabled={!data?.next_cursor}
            onClick={() => data?.next_cursor && setPages((p) => [...p, data.next_cursor!])}
          >
            Next
          </button>
        </div>
      </Card>

      {creating && <CreateReferral onClose={() => setCreating(false)} onCreated={() => { setCreating(false); reload(); }} />}
      {rechecking && (
        <BulkRecheck
          ids={[...picked]}
          onClose={() => setRechecking(false)}
          onDone={() => { setRechecking(false); setPicked(new Set()); reload(); }}
        />
      )}
      {accepting && (
        <BulkAccept
          ids={[...picked]}
          onClose={() => setAccepting(false)}
          onDone={() => { setAccepting(false); setPicked(new Set()); reload(); }}
        />
      )}
      {releasing && (
        <BulkRelease
          open={(id) => { setReleasing(false); open(id); }}
          onClose={() => { setReleasing(false); reload(); }}
          onShow={(value) => { setReleasing(false); setStatus(value); setPages([]); }}
        />
      )}
    </div>
  );
}

/**
 * Raise one referral by hand.
 *
 * For Zeeh's own use, not inDrive's — proving the driver-facing flow works,
 * or keying in something that arrived over WhatsApp during the pilot. It
 * goes through the same validation and the same `submit()` as the public
 * API; the only difference is `source: 'manual'`, so it's never mistaken for
 * real inDrive traffic in a report.
 *
 * Payout details are deliberately optional, same as the API: leave them out
 * and the referral lands in KYC_PENDING with a claim link the driver
 * completes themselves — which is exactly what's needed to test /claim.
 */
function CreateReferral({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [clientId, setClientId] = useState('indrive_ng');
  const [referringDriverId, setReferringDriverId] = useState('');
  const [referredDriverId, setReferredDriverId] = useState('');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [payoutChannel, setPayoutChannel] = useState<'' | 'bank' | 'wallet'>('');
  const [bankCode, setBankCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [walletProvider, setWalletProvider] = useState<'opay' | 'kuda' | 'palmpay' | 'moniepoint'>('opay');
  const [walletId, setWalletId] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ referralId: string; kycUrl: string | null } | null>(null);

  const kobo = parseNairaToKobo(amount);
  const sameDriver = referringDriverId.trim().length > 0 && referringDriverId.trim() === referredDriverId.trim();
  const canSubmit = clientId.trim() && referringDriverId.trim() && referredDriverId.trim() && phone.trim() && kobo && !sameDriver
    && (payoutChannel === '' || (payoutChannel === 'bank' ? bankCode.trim() && accountNumber.trim() : walletId.trim()));

  async function submit() {
    if (!kobo) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.createReferral({
        client_id: clientId.trim(),
        referring_driver_id: referringDriverId.trim(),
        referred_driver_id: referredDriverId.trim(),
        phone: phone.trim(),
        commission_amount: kobo,
        ...(payoutChannel === 'bank' ? { payout_channel: 'bank' as const, bank_code: bankCode.trim(), account_number: accountNumber.trim() } : {}),
        ...(payoutChannel === 'wallet' ? { payout_channel: 'wallet' as const, wallet_provider: walletProvider, wallet_id: walletId.trim() } : {}),
        ...(externalRef.trim() ? { external_ref: externalRef.trim() } : {}),
      });
      setResult({ referralId: r.referral_id, kycUrl: r.kyc_url ?? null });
    } catch (e) {
      /*
       * `body.message` alone is a generic "request failed validation" —
       * every actual reason lives in `details` (one per field). Without
       * this, typing the same id into both driver fields, say, shows
       * nothing more than that generic line and leaves whoever filled the
       * form guessing which field was wrong.
       */
      if (e instanceof RequestFailed && Array.isArray(e.body.details) && e.body.details.length > 0) {
        setError((e.body.details as { field?: string; message: string }[]).map((d) => (d.field ? `${d.field}: ${d.message}` : d.message)).join('; '));
      } else {
        setError((e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }

  if (result) {
    return (
      <Modal title="Referral created" onClose={onCreated} footer={<button className="btn" onClick={onCreated}>Done</button>}>
        <Banner tone="ok" title={`Referral ${result.referralId.slice(0, 8)} created`}>
          {result.kycUrl
            ? 'No payout account on file, so the driver finishes this themselves.'
            : 'Payout details were supplied — KYC will run automatically.'}
        </Banner>
        {result.kycUrl && (
          <div className="field" style={{ marginTop: 12 }}>
            <label>Direct link for this driver</label>
            <input readOnly value={result.kycUrl} onFocus={(e) => e.currentTarget.select()} className="mono" />
            <div className="small muted" style={{ marginTop: 6 }}>
              Or send them to the permanent link instead: <span className="mono">/claim</span> with their phone number,
              or <span className="mono">/claim?driver_id={referringDriverId.trim()}</span>.
            </div>
          </div>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      title="New referral"
      description="Same rules as inDrive's own API — this just skips having to write the request by hand."
      onClose={onClose}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn primary" onClick={submit} disabled={!canSubmit || busy}>
          {busy ? <span className="spin" /> : 'Create referral'}
        </button>
      </>}
    >
      {error && <Banner tone="danger" title="Could not create referral">{error}</Banner>}
      <div className="grid-2">
        <div className="field">
          <label>Client id</label>
          <input value={clientId} onChange={(e) => setClientId(e.target.value)} />
        </div>
        <div className="field">
          <label>External reference (optional)</label>
          <input value={externalRef} onChange={(e) => setExternalRef(e.target.value)} placeholder="e.g. a WhatsApp thread id" />
        </div>
        <div className="field">
          <label>Referring driver id (earns the commission)</label>
          <input value={referringDriverId} onChange={(e) => setReferringDriverId(e.target.value)} placeholder="inDrive's own id for them" />
        </div>
        <div className="field">
          <label>Referred driver id (the new driver)</label>
          <input value={referredDriverId} onChange={(e) => setReferredDriverId(e.target.value)} placeholder="inDrive's own id for them" />
          {sameDriver && <div className="hint" style={{ color: 'var(--danger)' }}>Must be different from the referring driver — a driver can't refer themselves.</div>}
        </div>
        <div className="field">
          <label>Referrer's phone number</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0803 123 4567" />
        </div>
        <div className="field">
          <label>Commission amount</label>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="₦5,000" />
        </div>
      </div>

      <div className="field" style={{ marginTop: 4 }}>
        <label>Payout account</label>
        <select value={payoutChannel} onChange={(e) => setPayoutChannel(e.target.value as typeof payoutChannel)}>
          <option value="">Unknown — let the driver supply it on the claim page</option>
          <option value="bank">Bank account, known already</option>
          <option value="wallet">Wallet, known already</option>
        </select>
      </div>

      {payoutChannel === 'bank' && (
        <div className="grid-2">
          <div className="field">
            <label>Bank code</label>
            <input value={bankCode} onChange={(e) => setBankCode(e.target.value)} placeholder="058 = GTBank, 044 = Access" />
          </div>
          <div className="field">
            <label>Account number</label>
            <input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} placeholder="10 digits" />
          </div>
        </div>
      )}

      {payoutChannel === 'wallet' && (
        <div className="grid-2">
          <div className="field">
            <label>Wallet provider</label>
            <select value={walletProvider} onChange={(e) => setWalletProvider(e.target.value as typeof walletProvider)}>
              <option value="opay">Opay</option>
              <option value="kuda">Kuda</option>
              <option value="palmpay">PalmPay</option>
              <option value="moniepoint">Moniepoint</option>
            </select>
          </div>
          <div className="field">
            <label>Wallet id</label>
            <input value={walletId} onChange={(e) => setWalletId(e.target.value)} />
          </div>
        </div>
      )}
    </Modal>
  );
}

function Row({ r, onOpen, checked, onToggle }: { r: Referral; onOpen: () => void; checked?: boolean; onToggle?: () => void }) {
  return (
    <tr className="clickable" onClick={onOpen} tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}>
      {onToggle && (
        <td onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" aria-label={`Select referral ${r.referral_id.slice(0, 8)}`} checked={checked ?? false} onChange={onToggle} />
        </td>
      )}
      <td>
        <div className="mono">{r.referral_id.slice(0, 8)}</div>
        {r.external_ref && <div className="small muted">{r.external_ref}</div>}
      </td>
      <td className="small">
        <div>{r.referring_driver_id ?? '—'}</div>
        <div className="muted">→ {r.referred_driver_id ?? '—'}</div>
      </td>
      <td>
        <Status value={r.status} />
        {r.waiting_reason && r.waiting_reason !== 'unchecked' && (
          <div className="small muted" style={{ marginTop: 4 }}>
            {r.waiting_reason === 'not_joined' ? 'Not joined yet'
              : r.waiting_reason === 'phone_differs' ? 'Joined · phone differs'
              : r.waiting_reason === 'sent_back' ? 'Sent back to fix'
              : 'Joined · BVN not confirmed'}
          </div>
        )}
      </td>
      <td className="num">{r.amount_display}</td>
      <td className="small">
        <span className="mono">{r.destination_masked}</span>
        <div className="muted">{r.payout_channel === 'bank' ? `Bank ${r.bank_code ?? ''}` : r.wallet_provider}</div>
      </td>
      <td>
        {r.reason_codes.length === 0
          ? <span className="muted small">—</span>
          : r.reason_codes.slice(0, 2).map((c) => <span className="code-chip" key={c}>{c}</span>)}
        {r.reason_codes.length > 2 && <span className="small muted">+{r.reason_codes.length - 2}</span>}
      </td>
      <td className="num small muted">{when(r.created_at)}</td>
    </tr>
  );
}


/**
 * Send payments for everything approved, in one go.
 *
 * Nothing is sent from here: confirming queues each payment, and the worker
 * sends them a few at a time through the same code a single release uses, with
 * your name as the recorded actor. What you confirm — the count and the total —
 * is checked again on the server, and the whole thing is refused if the list
 * has changed or the float cannot cover it.
 */
function BulkRelease({ onClose, onShow, open }: { onClose: () => void; onShow: (statusFilter: string) => void; open: (id: string) => void }) {
  const [phase, setPhase] = useState<'review' | 'running' | 'done'>('review');
  const { data, error, loading, reload } = useAsync(() => api.releasable(), []);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [started, setStarted] = useState<{ count: number; total: string } | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  // While payments go out, watch the list of approved-and-unpaid shrink.
  useEffect(() => {
    if (phase !== 'running') return;
    timer.current = setInterval(async () => {
      try {
        const s = await api.releasable();
        setRemaining(s.count);
        if (s.count === 0) { setPhase('done'); }
      } catch { /* a missed poll is fine; the next one will do */ }
    }, 4000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [phase]);

  async function confirm() {
    if (!data || data.count === 0) return;
    setBusy(true);
    setProblem(null);
    try {
      await api.bulkRelease({ referral_ids: data.referral_ids, expected_count: data.count, expected_total: data.total });
      setStarted({ count: data.count, total: data.total });
      setRemaining(data.count);
      setPhase('running');
    } catch (e) {
      setProblem((e as Error).message);
      reload();
    } finally {
      setBusy(false);
    }
  }

  const short = data && data.shortfall !== '0';

  if (phase !== 'review' && started) {
    const processed = remaining === null ? 0 : started.count - remaining;
    return (
      <Modal
        title={phase === 'done' ? 'All payments processed' : 'Sending payments'}
        description={phase === 'done' ? undefined : 'You can close this; the payments keep going.'}
        onClose={onClose}
        footer={
          phase === 'done'
            ? <>
                <button className="btn" onClick={() => onShow('PAYOUT_FAILED,FUNDS_HELD')}>Show needs attention</button>
                <button className="btn primary" onClick={() => onShow('PAYOUT_SUCCESS')}>Show paid</button>
              </>
            : <button className="btn" onClick={onClose}>Close</button>
        }
      >
        <dl className="dl">
          <dt>Payments</dt><dd style={{ fontWeight: 600 }}>{started.count}</dd>
          <dt>Total</dt><dd style={{ fontWeight: 600 }}>{formatKobo(started.total)}</dd>
          <dt>Processed</dt><dd>{processed} of {started.count}</dd>
        </dl>
        <div className="progress" style={{ margin: '8px 0 12px' }}><i style={{ width: `${started.count === 0 ? 0 : Math.round((processed / started.count) * 100)}%` }} /></div>
        {phase === 'done' && (
          <p className="small" style={{ lineHeight: 1.6 }}>
            “Processed” means each one has been sent or has stopped. Open <b>Show needs attention</b> to see any that did not go
            through and why; the others are paid.
          </p>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      title="Release all approved payments?"
      description="Moves real money out of inDrive's float, one transfer per driver."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || !data || data.count === 0 || Boolean(short)} onClick={confirm}>
            {busy ? <span className="spin" /> : data ? `Release ${data.count} · ${data.total_display}` : 'Release'}
          </button>
        </>
      }
    >
      {loading && <Loading />}
      {error && <Banner tone="danger" title="Could not load the list">{error}</Banner>}
      {problem && <Banner tone="danger" title="Nothing was sent">{problem}</Banner>}
      {data && data.count === 0 && <Banner tone="info">Nothing is ready to release in bulk.</Banner>}
      {data && data.count > 0 && (
        <>
          <dl className="dl">
            <dt>Payments</dt><dd style={{ fontWeight: 600 }}>{data.count}</dd>
            <dt>Total</dt><dd style={{ fontWeight: 600 }}>{data.total_display}</dd>
            <dt>Float available</dt><dd>{data.float_available_display ?? '—'}</dd>
          </dl>
          {short && (
            <Banner tone="danger" title="The float does not cover this">
              It is {data.shortfall_display} short. Top it up in Float &amp; ledger, then reopen this. Nothing will be sent until it covers the whole list.
            </Banner>
          )}
          {data.more && (
            <Banner tone="warn">There are more ready than one batch carries. This releases the oldest {data.count}; run it again for the rest.</Banner>
          )}
          <div className="table-wrap" style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--rule)', borderRadius: 'var(--r)', margin: '10px 0' }}>
            <table>
              <thead><tr><th>Driver</th><th>Name (inDrive / NIBSS)</th><th>Pays to</th><th className="num">Amount</th></tr></thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.referral_id}>
                    <td className="mono small">{r.driver_id ?? '—'}</td>
                    <td className="small"><NamePair r={r} /></td>
                    <td className="small">{r.destination_label} <span className="mono muted">{r.destination_masked}</span></td>
                    <td className="num">{r.amount_display}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="small" style={{ lineHeight: 1.6 }}>
            Only the payments listed above are sent. Your name is recorded against each one. A payment that fails is not retried by
            itself; it lands in Needs attention with the reason, and you retry it one at a time.
          </p>
        </>
      )}
      {data && data.held_count > 0 && (
        <Banner tone="warn" title={`${data.held_count} approved but not included`}>
          <div style={{ marginBottom: 6 }}>
            These carry a signal beyond missing screening (a phone mismatch counts unless NIBSS has inDrive's name), so each
            needs to be released by hand from its own page.
          </div>
          {data.held.slice(0, 8).map((r) => (
            <div key={r.referral_id} className="small" style={{ marginBottom: 4 }}>
              <button className="btn sm" onClick={() => open(r.referral_id)} style={{ marginRight: 8 }}>Open</button>
              <span className="mono">{r.driver_id ?? '—'}</span> · {r.amount_display} · {r.reasons_explained.map((x) => x.label).join('; ') || 'needs a look'}
              {(r.indrive_name || r.nibss_name) && <div style={{ marginLeft: 0, marginTop: 2 }}><NamePair r={r} /></div>}
            </div>
          ))}
          {data.held_count > 8 && <div className="small muted">and {data.held_count - 8} more</div>}
        </Banner>
      )}
    </Modal>
  );
}


/**
 * Accept the join-link details of several waiting drivers at once.
 *
 * Each driver is checked as if accepted alone: the BVN must still resolve at
 * NIBSS, and KYC then runs in full — account holder's name, sanctions, fraud.
 * Only the phone check is waived, by you, with one written reason recorded
 * against every referral. They are worked through a few at a time because each
 * needs live NIBSS lookups. Nothing is paid by this.
 */
function BulkAccept({ ids, onClose, onDone }: { ids: string[]; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [phase, setPhase] = useState<'form' | 'running' | 'done'>('form');
  const [results, setResults] = useState<{ referral_id: string; ok: boolean; message: string }[]>([]);
  const [problem, setProblem] = useState<string | null>(null);

  async function run() {
    if (reason.trim().length < 10) return;
    setPhase('running');
    setProblem(null);
    const CHUNK = 3;
    const all: typeof results = [];
    try {
      for (let i = 0; i < ids.length; i += CHUNK) {
        const r = await api.bulkAcceptEnrolments(ids.slice(i, i + CHUNK), reason.trim());
        all.push(...r.items);
        setResults([...all]);
      }
    } catch (e) {
      setProblem((e as Error).message);
    }
    setPhase('done');
  }

  const accepted = results.filter((r) => r.ok).length;
  const failed = results.filter((r) => !r.ok);

  return (
    <Modal
      title={phase === 'form' ? `Accept ${ids.length} driver${ids.length === 1 ? '' : 's'}?` : phase === 'running' ? 'Accepting…' : 'Done'}
      description={phase === 'form' ? 'NIBSS has their BVN under a different phone than inDrive gave us.' : undefined}
      onClose={phase === 'running' ? () => undefined : phase === 'done' ? onDone : onClose}
      footer={
        phase === 'form' ? (
          <>
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn danger" disabled={reason.trim().length < 10} onClick={run}>Accept and record</button>
          </>
        ) : phase === 'done' ? (
          <button className="btn primary" onClick={onDone}>Done</button>
        ) : undefined
      }
    >
      {phase === 'form' && (
        <>
          <p className="small" style={{ marginTop: 0, lineHeight: 1.6 }}>
            You are vouching that each of these is the inDrive driver on their referral — for example, inDrive confirmed their
            names. Only the phone check is waived. Each BVN is still checked at NIBSS, and KYC still checks the account holder's
            name, sanctions and fraud. Nothing is paid by this.
          </p>
          <div className="field">
            <label htmlFor="bulk-accept-reason">Why are you accepting them?</label>
            <textarea
              id="bulk-accept-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Eric Bonsu confirmed by email on 17 Oct that these drivers' names match inDrive's records."
            />
            <span className="hint">At least 10 characters. Your name and this reason are written to the audit log against every one.</span>
          </div>
        </>
      )}
      {phase !== 'form' && (
        <>
          <dl className="dl">
            <dt>Selected</dt><dd>{ids.length}</dd>
            <dt>Accepted</dt><dd style={{ fontWeight: 600 }}>{accepted}</dd>
            <dt>Not accepted</dt><dd>{failed.length}</dd>
          </dl>
          <div className="progress" style={{ margin: '8px 0 12px' }}><i style={{ width: `${Math.round((results.length / ids.length) * 100)}%` }} /></div>
          {problem && <Banner tone="danger" title="Stopped early">{problem}</Banner>}
          {failed.length > 0 && (
            <Banner tone="warn" title="These were not accepted">
              {failed.map((f) => <div key={f.referral_id} className="small" style={{ marginBottom: 4 }}><span className="mono">{f.referral_id.slice(0, 8)}</span> — {f.message}</div>)}
            </Banner>
          )}
        </>
      )}
    </Modal>
  );
}

/**
 * Re-check several waiting referrals against the join-link sign-ups.
 *
 * The same match as the Re-check button on one referral, run a few at a time:
 * a driver who has signed up and whose BVN NIBSS confirms moves on to KYC; one
 * who has not stays exactly where they were. Nothing is waived and nothing is paid.
 */
function BulkRecheck({ ids, onClose, onDone }: { ids: string[]; onClose: () => void; onDone: () => void }) {
  const [phase, setPhase] = useState<'form' | 'running' | 'done'>('form');
  const [results, setResults] = useState<{ referral_id: string; ok: boolean; matched: boolean; message: string }[]>([]);
  const [problem, setProblem] = useState<string | null>(null);

  async function run() {
    setPhase('running');
    setProblem(null);
    const CHUNK = 3;
    const all: typeof results = [];
    try {
      for (let i = 0; i < ids.length; i += CHUNK) {
        const r = await api.bulkRecheckEnrolments(ids.slice(i, i + CHUNK));
        all.push(...r.items);
        setResults([...all]);
      }
    } catch (e) {
      setProblem((e as Error).message);
    }
    setPhase('done');
  }

  const matched = results.filter((r) => r.matched);
  const stillWaiting = results.filter((r) => r.ok && !r.matched);
  const failed = results.filter((r) => !r.ok);

  return (
    <Modal
      title={phase === 'form' ? `Re-check ${ids.length} referral${ids.length === 1 ? '' : 's'}?` : phase === 'running' ? 'Re-checking…' : 'Done'}
      onClose={phase === 'running' ? () => undefined : phase === 'done' ? onDone : onClose}
      footer={
        phase === 'form' ? (
          <>
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn primary" onClick={run}>Re-check</button>
          </>
        ) : phase === 'done' ? (
          <button className="btn primary" onClick={onDone}>Done</button>
        ) : undefined
      }
    >
      {phase === 'form' && (
        <p className="small" style={{ marginTop: 0, lineHeight: 1.6 }}>
          Each referral is matched to the driver's join-link sign-up again. If the driver has signed up and NIBSS confirms their
          BVN is registered to the phone inDrive gave us, their details are used and KYC starts. If not, nothing changes and the
          reason is written to the referral's timeline. Nothing is paid.
        </p>
      )}
      {phase !== 'form' && (
        <>
          <dl className="dl">
            <dt>Checked</dt><dd>{results.length} of {ids.length}</dd>
            <dt>Matched, KYC started</dt><dd style={{ fontWeight: 600 }}>{matched.length}</dd>
            <dt>Still waiting</dt><dd>{stillWaiting.length}</dd>
            <dt>Could not check</dt><dd>{failed.length}</dd>
          </dl>
          <div className="progress" style={{ margin: '8px 0 12px' }}><i style={{ width: `${Math.round((results.length / ids.length) * 100)}%` }} /></div>
          {problem && <Banner tone="danger" title="Stopped early">{problem}</Banner>}
          {phase === 'done' && matched.length > 0 && (
            <Banner tone="ok" title="Matched">
              {matched.map((m) => <div key={m.referral_id} className="small mono" style={{ marginBottom: 2 }}>{m.referral_id.slice(0, 8)}</div>)}
            </Banner>
          )}
          {phase === 'done' && stillWaiting.length > 0 && (
            <p className="small muted" style={{ lineHeight: 1.6 }}>
              {stillWaiting.length} {stillWaiting.length === 1 ? 'is' : 'are'} still waiting. Nothing changed for {stillWaiting.length === 1 ? 'it' : 'them'};
              open a referral to see why on its timeline.
            </p>
          )}
          {failed.length > 0 && (
            <Banner tone="warn" title="These could not be re-checked">
              {failed.map((f) => <div key={f.referral_id} className="small" style={{ marginBottom: 4 }}><span className="mono">{f.referral_id.slice(0, 8)}</span> — {f.message}</div>)}
            </Banner>
          )}
        </>
      )}
    </Modal>
  );
}

const PAIR_TONE = { same: 'ok', likely: 'warn', different: 'danger' } as const;
const PAIR_LABEL = { same: 'Same name', likely: 'Close — check it', different: 'Different name' } as const;

/** inDrive's name and NIBSS's for the driver being paid, with the verdict. */
function NamePair({ r }: { r: { indrive_name: string | null; nibss_name: string | null; name_verdict: 'same' | 'likely' | 'different' | null } }) {
  return (
    <span>
      {r.indrive_name ?? <span className="muted">no inDrive name</span>}
      <span className="muted"> / </span>
      {r.nibss_name ?? <span className="muted">no NIBSS name</span>}
      {r.name_verdict && <> <span className={`pill ${PAIR_TONE[r.name_verdict]}`}>{PAIR_LABEL[r.name_verdict]}</span></>}
    </span>
  );
}
